import React from 'react';
import PropTypes from 'prop-types';

const Strings = require('../../../../../app/shared/Strings');
const StringUtil = require('../../../../../app/shared/StringUtil');
const C3ColorUtil = require('../../../util/C3ColorUtil');
import DonutChart from '../../chart/DonutChart';
import BarChart from '../../chart/BarChart';

const propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
    chartWidth: PropTypes.number,
    chartHeight: PropTypes.number,
	chartType: PropTypes.oneOf(['donut', 'bar']),
	chartAlign: PropTypes.oneOf(['left', 'center']),
	onChangeChartType: PropTypes.func.isRequired,
    avgTimePlayed: PropTypes.number,
    attemptsCompleted: PropTypes.number,
    avgScore: PropTypes.number,
    timePlayedGraphData: PropTypes.array,
    completionGraphData: PropTypes.array,
    scoreGraphData: PropTypes.array,
    scoreCategoryGraphData: PropTypes.array,
};
const defaultProps = {
	id: 'charts',
	className: '',
    avgTimePlayed: 0,
    attemptsCompleted: 0,
    avgScore: 0,
    timePlayedGraphData: [],
    completionGraphData: [],
    scoreGraphData: [],
    chartWidth: 185,
    chartHeight: 185,
    chartAlign: 'center',
};

export default function QLAUsageCharts(props) {
	let charts;
	let chartRowClass;
	let extraChartClasses;
	const scoreColorsObj = C3ColorUtil.getScoreRangeColorObj();
	const scoreLabelColorsObj = C3ColorUtil.getScoreRangeLabelColorObj();
	const labelRatio = 1;
	const selectorUI = (
		<label htmlFor={`${props.id}-type-select`}>
			{Strings['en'].activity.qla.charts.CH_TYPE}
			<div id={`${props.id}-type-select`} className="margin-top-md" role="radiogroup">
					<div className="inline-block margin-right-lg">
                        <input
                            id={`${props.id}-type-select-donut`}
                            type="radio"
                            value="donut"
                            checked={props.chartType === 'donut'}
                            onChange={props.onChangeChartType}
                        />
                        <label className="block" htmlFor={`${props.id}-type-select-donut`}>
                        	{Strings['en'].activity.qla.charts.CH_TYPE_PIE}
                        </label>
                    </div>

                    <div className="inline-block">
                        <input
                            id={`${props.id}-type-select-bar`}
                            type="radio"
                            value="bar"
                            checked={props.chartType === 'bar'}
                            onChange={props.onChangeChartType}
                        />
                        <label className="block" htmlFor={`${props.id}-type-select-bar`}>
                        	{Strings['en'].activity.qla.charts.CH_TYPE_BAR}
                        </label>
                    </div>
            </div>
        </label>
	);

	if (props.chartAlign === 'left') {
		chartRowClass = 'qlaOverallChartRow--left';
		extraChartClasses = 'qlaLeftChart';
	}
	else if (props.chartAlign === 'center') {
		chartRowClass = 'qlaOverallChartRow';
	}
	else {
		chartRowClass = 'qlaOverallChartRow';
	}

	if (props.chartType === 'donut') {
		charts = (
			<div className={chartRowClass}>
	            <DonutChart
		            id="chart-complete-percent"
		            className={extraChartClasses}
		            chartClassName="mzl-bb-donut-sm-label"
		            width={props.chartWidth}
		            height={props.chartHeight}
		            title={Strings['en'].activity.qla.charts.CH_TITLE_COMPLETED}
		            columns={props.completionGraphData}
		            colors={C3ColorUtil.getNonScoringColorsForData(props.completionGraphData)}
		            labelRatio={labelRatio}
		            summaryStat={props.attemptsCompleted}
		            tooltip={Strings['en'].activity.qla.charts.CH_TT_COMPLETED}
	            />
	            <DonutChart
		            id="chart-timeplayed"
		            className={extraChartClasses}
		            chartClassName="mzl-bb-donut-sm-label"
		            width={props.chartWidth}
		            height={props.chartHeight}
		            title={Strings['en'].activity.qla.charts.CH_TITLE_TP}
		            columns={props.timePlayedGraphData}
		            colors={C3ColorUtil.getNonScoringColorsForData(props.timePlayedGraphData)}
		            labelRatio={labelRatio}
		            summaryStat={StringUtil.getTimeStringForMs(props.avgTimePlayed)}
		            tooltip={Strings['en'].activity.qla.charts.CH_TT_TP}
	            />
	            <DonutChart
		            id="chart-score"
		            className={extraChartClasses}
		            chartClassName="mzl-bb-donut-sm-label"
		            width={props.chartWidth}
		            height={props.chartHeight}
		            title={Strings['en'].activity.qla.charts.CH_TITLE_SCORE}
		            columns={props.scoreGraphData}
		            colors={scoreColorsObj}
		            labelRatio={labelRatio}
		            labelColors={scoreLabelColorsObj}
		            summaryStat={StringUtil.formatRawScore(props.avgScore, 'percent')}
		            tooltip={Strings['en'].activity.qla.charts.CH_TT_SCORE}
	            />
				{(props.scoreCategoryGraphData.length > 0) &&
		            <DonutChart
			            id="chart-score-categories"
			            className={extraChartClasses}
			            chartClassName="mzl-bb-donut-sm-label"
			            width={props.chartWidth}
			            height={props.chartHeight}
			            title={Strings['en'].activity.qla.charts.CH_TITLE_SC}
			            labelRatio={labelRatio}
			            columns={props.scoreCategoryGraphData}
			            colors={C3ColorUtil.getNonScoringColorsForData(props.scoreCategoryGraphData)}
			            tooltip={Strings['en'].activity.qla.charts.CH_TT_SC}
		            />
		        }
		        {selectorUI}
			</div>
		);
	}
	else if (props.chartType === 'bar') {
		charts = (
			<div className={chartRowClass}>
	            <BarChart
		            id="chart-complete-percent"
		            className={extraChartClasses}
		            removeXTick
		            yInt
		            xAxisType="category"
		            width={props.chartWidth}
		            height={props.chartHeight}
		            title={Strings['en'].activity.qla.charts.CH_TITLE_COMPLETED}
		            columns={props.completionGraphData}
		            colors={C3ColorUtil.getNonScoringColorsForData(props.completionGraphData)}
		            tooltip={Strings['en'].activity.qla.charts.CH_TT_COMPLETED}
	            />
	            <BarChart
		            id="chart-timeplayed"
		            className={extraChartClasses}
		            removeXTick
		            yInt
		            xAxisType="category"
		            width={props.chartWidth}
		            height={props.chartHeight}
		            title={Strings['en'].activity.qla.charts.CH_TITLE_TP}
		            columns={props.timePlayedGraphData}
		            colors={C3ColorUtil.getNonScoringColorsForData(props.timePlayedGraphData)}
		            tooltip={Strings['en'].activity.qla.charts.CH_TT_TP}
	            />
	            <BarChart
		            id="chart-score"
		            className={extraChartClasses}
		            removeXTick
		            yInt
		            xAxisType="category"
		            width={props.chartWidth}
		            height={props.chartHeight}
		            title={Strings['en'].activity.qla.charts.CH_TITLE_SCORE}
		            columns={props.scoreGraphData}
		            colors={scoreColorsObj}
		            tooltip={Strings['en'].activity.qla.charts.CH_TT_SCORE}
	            />
				{(props.scoreCategoryGraphData.length > 0) &&
		            <BarChart
			            id="chart-score-categories"
			            className={extraChartClasses}
			            removeXTick
			            yInt
			            xAxisType="category"
			            width={props.chartWidth}
			            height={props.chartHeight}
			            title={Strings['en'].activity.qla.charts.CH_TITLE_SC}
			            columns={props.scoreCategoryGraphData}
			            colors={C3ColorUtil.getNonScoringColorsForData(props.scoreCategoryGraphData)}
			            tooltip={Strings['en'].activity.qla.charts.CH_TT_SC}
		            />
		        }
		        {selectorUI}
			</div>
		);

	}
	else {
		return null;
	}

	return (
		<div id={props.id} className={props.className}>
			{charts}
		</div>
	)	
}

QLAUsageCharts.propTypes = propTypes;
QLAUsageCharts.defaultProps = defaultProps;